import { useMsal } from "@azure/msal-react";
import { ModalProps } from "antd";
import React from "react";
import styled from "styled-components";

import { COLORS } from "../constants";
import { publicClientAppScopes } from "../utils";

import { Button } from "./Button";
import { ConnectCoeffIcon, ExcelSheetsIcon, Lock } from "./Icons";
import { message } from "./message";
import { Modal } from "./Modal";
import { Typography } from "./Typography";

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 8px;
    box-shadow: 0px 0px 16px 0px ${COLORS.black8}, 0px 16px 24px 0px ${COLORS.black8};
  }

  .ant-modal-close {
    top: 17px;
    right: 20px;
  }

  .ant-modal-close-x {
    width: 12px;
    height: 12px;
    line-height: 12px;
  }
`;

const Content = styled.div`
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
`;

const HeaderIcons = styled.div`
  position: relative;
  width: 147px;
  height: 60px;
`;

const ConnectIconWrap = styled.div`
  position: absolute;
  left: 0;
  top: 5px;
  width: 75px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MicrosoftIconCircle = styled.div`
  position: absolute;
  left: 87px;
  top: 0;
  width: 60px;
  height: 60px;
  border-radius: 9999px;
  background: ${COLORS.greenBg5};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Footer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  line-height: 1.3;
  margin-top: 4px;
  width: 100%;
  text-align: left;
`;

const AuthorizeButton = styled(Button)`
  width: 100%;
  height: 32px;
  border-radius: 4px;
  font-size: 14px;
  line-height: 18px;
`;

const EXTRA_SCOPES_TO_CONSENT = [
  "offline_access",
  "Files.ReadWrite.All",
  "Sites.ReadWrite.All",
  "User.Read",
  "profile",
  "email",
  "openid",
];

type Props = ModalProps & {
  onSuccess?: () => void;
};

export const MicrosoftAuthModal: React.FC<Props> = ({ onSuccess, ...rest }) => {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = React.useState(false);

  const handleAuthorize = async () => {
    try {
      setIsLoading(true);

      const res = await instance.loginPopup({
        scopes: publicClientAppScopes,
        extraScopesToConsent: EXTRA_SCOPES_TO_CONSENT,
        prompt: "consent",
      });

      if (res?.account) {
        instance.setActiveAccount(res.account);
      }

      onSuccess?.();
    } catch (error) {
      console.error(error);
      message.error("Failed to authorize Microsoft. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <StyledModal {...rest} padding="60px 20px 20px" width={400}>
      <Content>
        <HeaderIcons>
          <ConnectIconWrap>
            <ConnectCoeffIcon width="75" height="50" />
          </ConnectIconWrap>
          <MicrosoftIconCircle>
            <ExcelSheetsIcon width="32" height="32" />
          </MicrosoftIconCircle>
        </HeaderIcons>

        <div>
          <Typography fontSize="18px" lineHeight="22px" fontWeight={700}>
            Connect Microsoft
          </Typography>
          <Typography fontSize="14px" lineHeight="18px" fontWeight={500} color="textSecondary">
            You need to authorize Coefficient to access OneDrive/Excel in order to build and refresh
            this dashboard.
          </Typography>
        </div>

        <AuthorizeButton
          disabled={isLoading}
          block
          type="primary"
          onClick={() => void handleAuthorize()}
        >
          Authorize
        </AuthorizeButton>

        <Footer>
          <div
            style={{
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Lock fill={COLORS.black} />
          </div>

          <Typography
            fontSize="12px"
            lineHeight="14px"
            fontWeight={600}
            color="textSecondary"
            align="left"
          >
            Coefficient encrypts all of your credentials end-to-end, including Auth tokens, API
            Keys, and passwords.
          </Typography>
        </Footer>
      </Content>
    </StyledModal>
  );
};
